import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { Dialog } from '@/shared/ui/dialog';
import { Field } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';
import { Alert } from '@/shared/ui/alert';
import { requestAccessSchema, type RequestAccessValues } from '../types/request-access';
import { useRequestAccess, requestAccessErrorMessage } from '../hooks/use-request-access';

interface RequestAccessModalProps {
  open: boolean;
  onClose: () => void;
}

export function RequestAccessModal({ open, onClose }: RequestAccessModalProps) {
  const [done, setDone] = useState(false);
  const mutation = useRequestAccess();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RequestAccessValues>({
    resolver: zodResolver(requestAccessSchema),
    defaultValues: { email: '' },
  });

  function close() {
    onClose();
    // Reset depois de fechar (evita flicker do estado de sucesso ao reabrir).
    setTimeout(() => {
      setDone(false);
      reset();
      mutation.reset();
    }, 150);
  }

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(values.email);
    setDone(true);
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Solicitar acesso"
      description={
        done ? undefined : 'Informe seu e-mail. Se houver um cadastro elegível, um administrador vai liberar seu acesso.'
      }
    >
      {done ? (
        <div className="space-y-5">
          <Alert
            variant="success"
            message="Recebemos sua solicitação. Se o e-mail estiver elegível, um administrador vai liberar seu acesso — você será avisado por e-mail."
          />
          <Button type="button" size="lg" onClick={close}>
            Entendi
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <Field label="E-mail" htmlFor="request-access-email" error={errors.email?.message}>
            <Input
              id="request-access-email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="voce@empresa.com"
              error={!!errors.email}
              {...register('email')}
            />
          </Field>

          {mutation.isError && (
            <Alert variant="error" message={requestAccessErrorMessage(mutation.error)} />
          )}

          <Button type="submit" size="lg" loading={isSubmitting || mutation.isPending}>
            Enviar solicitação
            {!(isSubmitting || mutation.isPending) && <ArrowRight className="h-[18px] w-[18px]" />}
          </Button>
        </form>
      )}
    </Dialog>
  );
}
